import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import StorefrontOutlined from '@mui/icons-material/StorefrontOutlined';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, unwrapJson } from '../../../api/client';
import { EmptyState } from '../../../components/states';
import { moneyCompact, qty as fmtQty } from '../../../lib/format';
import { useShop } from '../../../shop/ShopProvider';
import { RankingBars, PeriodBars } from '../components/charts';
import { ReportFrame } from '../components/ReportFrame';
import { ReportTable } from '../components/ReportTable';
import { Panel, QueryError, Segmented, StatGrid, StatTile } from '../components/ui';
import { useReportParams, usePeriodText, type ReportParams } from '../components/useReportParams';
import { fetchCsv, reportFileName } from '../lib/csv';
import { periodLabel } from '../lib/dateRange';
import { num, share } from '../lib/numbers';
import { ItemCell, M, Q, RangeControls } from './common';
import { PartyDocumentRows } from './PartyDocuments';

type Source = 'bills' | 'receipts';

const emptyState = (
  <EmptyState
    icon={<LocalShippingOutlined />}
    title="No purchases in this period"
    body="Try a longer date range, or record a supplier bill to see it here."
  />
);

function useSource(p: ReportParams): Source {
  return p.get('source') === 'receipts' ? 'receipts' : 'bills';
}

function SourceToggle({ p, source }: { p: ReportParams; source: Source }) {
  return (
    <Segmented<Source>
      label="Based on"
      value={source}
      options={[
        { value: 'bills', label: 'Supplier bills' },
        { value: 'receipts', label: 'Goods received' },
      ]}
      onChange={(v) => p.update({ source: v === 'bills' ? null : v })}
    />
  );
}

const docWord = (source: Source) => (source === 'bills' ? 'Bills' : 'Receipts');

export function PurchasesByPeriodReport() {
  const p = useReportParams('30d');
  const { format } = useShop();
  const source = useSource(p);
  const query = { ...p.range, interval: p.interval, source };
  const q = useQuery({
    queryKey: ['reports', 'purchases-by-period', query],
    queryFn: () => unwrapJson(api.GET('/reports/purchases/by-period', { params: { query } })),
    placeholderData: keepPreviousData,
  });
  const rows = q.data?.data;
  const t = q.data?.totals;
  return (
    <ReportFrame
      slug="purchases-by-period"
      period={usePeriodText(p)}
      controls={
        <>
          <RangeControls p={p} interval />
          <SourceToggle p={p} source={source} />
        </>
      }
      onExport={() =>
        fetchCsv(
          api.GET('/reports/purchases/by-period', {
            params: { query: { ...query, format: 'csv' } },
            parseAs: 'text',
          }),
          reportFileName(`purchases-by-${p.interval}`, p.range),
        )
      }
    >
      {q.error && <QueryError error={q.error} onRetry={() => void q.refetch()} />}
      <StatGrid>
        <StatTile
          label="Total purchases"
          value={t && moneyCompact(t.total, format)}
          hint={t && `Net ${moneyCompact(t.net, format)} · tax ${moneyCompact(t.tax, format)}`}
          icon={<LocalShippingOutlined />}
          loading={q.isLoading}
        />
        <StatTile
          label={docWord(source)}
          value={t?.documentCount}
          hint={t && `${fmtQty(t.qty, format)} units`}
          icon={<ReceiptLongRounded />}
          tone="info"
          loading={q.isLoading}
        />
        <StatTile
          label="Average document"
          value={t && moneyCompact(t.documentCount ? num(t.total) / t.documentCount : 0, format)}
          hint={source === 'bills' ? 'Per supplier bill' : 'Per goods receipt'}
          icon={<Inventory2Outlined />}
          tone="success"
          loading={q.isLoading}
        />
      </StatGrid>
      <Panel title="Purchases trend" subtitle="Total per period" sx={{ mb: 2.5 }}>
        <PeriodBars
          loading={q.isLoading}
          labels={(rows ?? []).map((r) => periodLabel(p.interval, r.periodStart, format.locale))}
          series={[
            {
              key: 'total',
              label: 'Total',
              data: (rows ?? []).map((r) => num(r.total)),
              brand: true,
            },
          ]}
        />
      </Panel>
      <ReportTable
        loading={q.isLoading}
        rows={rows ? rows.filter((r) => r.documentCount > 0).reverse() : undefined}
        rowKey={(r) => r.periodStart}
        empty={emptyState}
        columns={[
          {
            key: 'period',
            header: 'Period',
            render: (r) => periodLabel(p.interval, r.periodStart, format.locale, true),
          },
          {
            key: 'count',
            header: docWord(source),
            align: 'right',
            render: (r) => r.documentCount,
            footer: t?.documentCount,
          },
          {
            key: 'qty',
            header: 'Units',
            align: 'right',
            hideBelow: 'md',
            render: (r) => <Q v={r.qty} />,
            footer: <Q v={t?.qty} />,
          },
          {
            key: 'net',
            header: 'Net',
            align: 'right',
            hideBelow: 'sm',
            render: (r) => <M v={r.net} />,
            footer: <M v={t?.net} />,
          },
          {
            key: 'tax',
            header: 'Tax',
            align: 'right',
            hideBelow: 'md',
            render: (r) => <M v={r.tax} />,
            footer: <M v={t?.tax} />,
          },
          {
            key: 'total',
            header: 'Total',
            align: 'right',
            render: (r) => <M v={r.total} strong />,
            footer: <M v={t?.total} />,
          },
        ]}
      />
    </ReportFrame>
  );
}

export function PurchasesBySupplierReport() {
  const p = useReportParams('thisMonth');
  const { format } = useShop();
  const source = useSource(p);
  const query = { ...p.range, source, page: p.page, pageSize: p.pageSize };
  const q = useQuery({
    queryKey: ['reports', 'purchases-by-supplier', query],
    queryFn: () => unwrapJson(api.GET('/reports/purchases/by-supplier', { params: { query } })),
    placeholderData: keepPreviousData,
  });
  const t = q.data?.totals;
  const total = num(t?.total);
  return (
    <ReportFrame
      slug="purchases-by-supplier"
      period={usePeriodText(p)}
      controls={
        <>
          <RangeControls p={p} />
          <SourceToggle p={p} source={source} />
        </>
      }
      onExport={() =>
        fetchCsv(
          api.GET('/reports/purchases/by-supplier', {
            params: { query: { ...p.range, source, format: 'csv' } },
            parseAs: 'text',
          }),
          reportFileName('purchases-by-supplier', p.range),
        )
      }
    >
      {q.error && <QueryError error={q.error} onRetry={() => void q.refetch()} />}
      <StatGrid>
        <StatTile
          label="Total purchases"
          value={t && moneyCompact(t.total, format)}
          icon={<LocalShippingOutlined />}
          loading={q.isLoading}
        />
        <StatTile
          label="Suppliers"
          value={q.data?.page.total}
          hint="Bought from in this period"
          icon={<StorefrontOutlined />}
          tone="info"
          loading={q.isLoading}
        />
        <StatTile
          label={docWord(source)}
          value={t?.documentCount}
          icon={<ReceiptLongRounded />}
          tone="success"
          loading={q.isLoading}
        />
      </StatGrid>
      <Panel title="Top suppliers" subtitle="By purchase total" sx={{ mb: 2.5 }}>
        <RankingBars
          loading={q.isLoading}
          items={(p.page === 1 ? (q.data?.data ?? []) : []).map((r) => ({
            label: r.name,
            value: num(r.total),
          }))}
        />
      </Panel>
      <ReportTable
        loading={q.isLoading}
        rows={q.data?.data}
        rowKey={(r) => r.contactId}
        empty={emptyState}
        page={q.data?.page}
        onPageChange={p.setPage}
        onPageSizeChange={p.setPageSize}
        expand={(r, subRow) => (
          <PartyDocumentRows
            side="purchases"
            contactId={r.contactId}
            range={p.range}
            source={source}
            subRow={subRow}
          />
        )}
        columns={[
          { key: 'name', header: 'Supplier', render: (r) => <ItemCell name={r.name} /> },
          {
            key: 'count',
            header: docWord(source),
            align: 'right',
            hideBelow: 'sm',
            render: (r) => r.documentCount,
            footer: t?.documentCount,
          },
          {
            key: 'net',
            header: 'Net',
            align: 'right',
            hideBelow: 'md',
            render: (r) => <M v={r.net} />,
            footer: <M v={t?.net} />,
          },
          {
            key: 'tax',
            header: 'Tax',
            align: 'right',
            hideBelow: 'md',
            render: (r) => <M v={r.tax} />,
            footer: <M v={t?.tax} />,
          },
          {
            key: 'total',
            header: 'Total',
            align: 'right',
            render: (r) => <M v={r.total} strong />,
            footer: <M v={t?.total} />,
          },
          {
            key: 'share',
            header: 'Share',
            align: 'right',
            hideBelow: 'sm',
            render: (r) => share(num(r.total), total),
            footer: total ? '100%' : '—',
          },
        ]}
      />
    </ReportFrame>
  );
}

export function PurchasesByProductReport() {
  const p = useReportParams('thisMonth');
  const { format } = useShop();
  const source = useSource(p);
  const groupBy = p.get('groupBy') === 'product' ? 'product' : 'variant';
  const query = { ...p.range, source, groupBy, page: p.page, pageSize: p.pageSize } as const;
  const q = useQuery({
    queryKey: ['reports', 'purchases-by-product', query],
    queryFn: () => unwrapJson(api.GET('/reports/purchases/by-product', { params: { query } })),
    placeholderData: keepPreviousData,
  });
  const t = q.data?.totals;
  const total = num(t?.total);
  return (
    <ReportFrame
      slug="purchases-by-product"
      period={usePeriodText(p)}
      controls={
        <>
          <RangeControls p={p} />
          <SourceToggle p={p} source={source} />
          <Segmented
            label="Group by"
            value={groupBy}
            options={[
              { value: 'variant', label: 'Per variant' },
              { value: 'product', label: 'Per product' },
            ]}
            onChange={(v) => p.update({ groupBy: v === 'variant' ? null : v })}
          />
        </>
      }
      onExport={() =>
        fetchCsv(
          api.GET('/reports/purchases/by-product', {
            params: { query: { ...p.range, source, groupBy, format: 'csv' } },
            parseAs: 'text',
          }),
          reportFileName('purchases-by-product', p.range),
        )
      }
    >
      {q.error && <QueryError error={q.error} onRetry={() => void q.refetch()} />}
      <StatGrid>
        <StatTile
          label="Total purchases"
          value={t && moneyCompact(t.total, format)}
          icon={<LocalShippingOutlined />}
          loading={q.isLoading}
        />
        <StatTile
          label={groupBy === 'product' ? 'Products bought' : 'Variants bought'}
          value={q.data?.page.total}
          icon={<Inventory2Outlined />}
          tone="info"
          loading={q.isLoading}
        />
        <StatTile
          label="Tax paid"
          value={t && moneyCompact(t.tax, format)}
          icon={<ReceiptLongRounded />}
          tone="success"
          loading={q.isLoading}
        />
      </StatGrid>
      <Panel title="Top items" subtitle="By purchase total" sx={{ mb: 2.5 }}>
        <RankingBars
          loading={q.isLoading}
          items={(p.page === 1 ? (q.data?.data ?? []) : []).map((r) => ({
            label: r.name,
            value: num(r.total),
          }))}
        />
      </Panel>
      <ReportTable
        loading={q.isLoading}
        rows={q.data?.data}
        rowKey={(r) => r.variantId ?? r.productId}
        empty={emptyState}
        page={q.data?.page}
        onPageChange={p.setPage}
        onPageSizeChange={p.setPageSize}
        columns={[
          { key: 'name', header: 'Item', render: (r) => <ItemCell name={r.name} sku={r.sku} /> },
          {
            key: 'qty',
            header: 'Qty',
            align: 'right',
            render: (r) => <Q v={r.qty} unit={r.unitSymbol} />,
          },
          {
            key: 'count',
            header: docWord(source),
            align: 'right',
            hideBelow: 'md',
            render: (r) => r.documentCount,
          },
          {
            key: 'net',
            header: 'Net',
            align: 'right',
            hideBelow: 'md',
            render: (r) => <M v={r.net} />,
            footer: <M v={t?.net} />,
          },
          {
            key: 'tax',
            header: 'Tax',
            align: 'right',
            hideBelow: 'lg',
            render: (r) => <M v={r.tax} />,
            footer: <M v={t?.tax} />,
          },
          {
            key: 'total',
            header: 'Total',
            align: 'right',
            render: (r) => <M v={r.total} strong />,
            footer: <M v={t?.total} />,
          },
          {
            key: 'share',
            header: 'Share',
            align: 'right',
            hideBelow: 'sm',
            render: (r) => share(num(r.total), total),
          },
        ]}
      />
    </ReportFrame>
  );
}
